import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Volume2, VolumeX, Loader2, Clock, Check } from "lucide-react";
import { playBeep, isSoundOn, setSound } from "../lib/sound";

const GOLD = "#C9A24B";
const hhmm = (iso) => (iso ? new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }) : "");

export default function KdsBoard({ station, title, Icon }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sound, setSnd] = useState(isSoundOn());
  const [busy, setBusy] = useState("");
  const [, force] = useState(0);
  const knownRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/kds/orders", { params: { station } });
      const ids = new Set(data.map((o) => o.id));
      if (knownRef.current) {
        for (const o of data) { if (!knownRef.current.has(o.id)) { playBeep(); break; } }
      }
      knownRef.current = ids;
      setOrders(data);
    } catch { /* noop */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    knownRef.current = null; setLoading(true); load();
    const id = setInterval(load, 3000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [station]);
  useEffect(() => { const t = setInterval(() => force((x) => x + 1), 15000); return () => clearInterval(t); }, []);

  const serve = async (oid) => {
    setBusy(oid);
    try { await api.post(`/orders/${oid}/serve-station`, { station }); await load(); }
    catch { /* noop */ }
    finally { setBusy(""); }
  };
  const toggleSound = () => { const n = !sound; setSnd(n); setSound(n); };

  return (
    <div data-testid={`kds-${station}`}>
      <div className="flex items-center justify-between mb-5">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Monitor</p>
          <h1 className="font-dash text-3xl font-semibold flex items-center gap-2"><Icon className="w-7 h-7" style={{ color: GOLD }} /> {title}</h1>
        </div>
        <Button variant="outline" size="icon" className="rounded-full" onClick={toggleSound} title="Suono" data-testid="kds-sound-toggle">
          {sound ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5 text-muted-foreground" />}
        </Button>
      </div>

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      ) : orders.length === 0 ? (
        <Card className="p-10 rounded-xl text-center text-muted-foreground" data-testid="kds-empty">Nessuna comanda da lavorare per {title}.</Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {orders.map((o) => {
            const mins = o.created_at ? Math.floor((Date.now() - new Date(o.created_at).getTime()) / 60000) : 0;
            const old = mins >= 10;
            return (
              <Card key={o.id} className={`p-4 rounded-2xl flex flex-col ${old ? "ring-2 ring-destructive/60" : "border-border"}`} data-testid={`kds-order-${o.table_number}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-dash text-2xl font-semibold leading-none">Tavolo {o.table_number}</p>
                    <p className="text-xs text-muted-foreground mt-1">{o.order_code}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm">{hhmm(o.created_at)}</p>
                    <p className={`text-xs font-medium ${old ? "text-destructive" : "text-muted-foreground"}`}><Clock className="w-3 h-3 inline -mt-0.5" /> {mins} min</p>
                  </div>
                </div>
                {o.notes && <p className="text-xs mt-2 text-amber-600">📝 {o.notes}</p>}
                <div className="mt-3 space-y-3 flex-1">
                  {(() => {
                    const byCourse = {};
                    o.items.forEach((i) => { const c = i.course || 1; (byCourse[c] = byCourse[c] || []).push(i); });
                    const courseNums = Object.keys(byCourse).map(Number).sort((a, b) => a - b);
                    const multi = courseNums.length > 1;
                    return courseNums.map((cn) => {
                      const cst = (o.courses && o.courses[String(cn)]) || {};
                      const fired = cst.status === "fired" || byCourse[cn].some((x) => x.fired);
                      return (
                        <div key={cn} data-testid={`kds-course-${o.table_number}-${cn}`}>
                          {multi && (
                            <div className={`flex items-center gap-2 mb-1.5 px-2 py-1 rounded-md text-xs font-bold uppercase tracking-wide ${fired ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-500"}`}>
                              USCITA {cn} · {fired ? "PREPARARE ORA" : "DOPO"}
                            </div>
                          )}
                          {!multi && !fired && (
                            <div className="flex items-center gap-2 mb-1.5 px-2 py-1 rounded-md text-xs font-bold uppercase tracking-wide bg-zinc-100 text-zinc-500">
                              USCITA {cn} · DOPO
                            </div>
                          )}
                          <div className={`space-y-2 ${!fired ? "opacity-55" : ""}`}>
                            {byCourse[cn].map((i) => (
                              <div key={i.item_id} className="flex items-start justify-between gap-2 border-b border-border/60 pb-2 last:border-0" data-testid={`kds-item-${i.item_id}`}>
                                <div className="min-w-0">
                                  <span className="font-medium text-lg">{i.quantity}× {i.name}</span>
                                  {i.modifiers?.length > 0 && <span className="block text-xs text-muted-foreground">+ {i.modifiers.join(", ")}</span>}
                                  {i.notes && <span className="block text-xs text-amber-600">📝 {i.notes}</span>}
                                </div>
                                {i.round > 1 && <span className="shrink-0 text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">giro {i.round}</span>}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
                <Button className="w-full mt-4 rounded-full h-11 font-semibold" style={{ backgroundColor: GOLD, color: "#14110E" }}
                  disabled={busy === o.id} onClick={() => serve(o.id)} data-testid={`kds-serve-${o.id}`}>
                  {busy === o.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" /> SERVITO</>}
                </Button>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
